import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthState } from 'src/app/states/auth.state';
import { Utility, Guid, setTimeout$ } from 'src/app/core/common';
import { Kind, JobStatus, ModelType, SearchParams } from 'src/app/common';
import { ApplyService } from 'src/app/services/apply.service';
import { CollectService } from 'src/app/services/collect.service';
import { FavoriteService } from 'src/app/services/favorite.service';
import { JobService } from 'src/app/services/job.service';
import { SharedService } from 'src/app/services/shared.service';
import { SpinnerService } from 'src/app/services/spinner.service';
import { environment } from 'src/environments/environment';
import { AnalyticsService } from 'src/app/services/analytics.service';
import { KarpashaService } from 'src/app/services/karpasha.service';
import { Constants } from 'src/app/states/constants';

declare const $: Function;

@Component({
  selector: 'app-job-detail',
  templateUrl: './job-detail.component.html',
  styleUrls: ['./job-detail.component.scss']
})
export class JobDetailComponent implements OnInit, OnDestroy {
  Kind = Kind;
  Utility = Utility;
  history = history;
  JobStatus = JobStatus;
  imageUrl = environment.app.filesUrl;
  dringTranslation = Constants.Translations.drivingLicense;

  @Input() preview = false;
  @Input() favorite: boolean;
  @Output() favoriteChange = new EventEmitter<boolean>();
  @Input('job-id') set jobId(jobId: string) { this.load(jobId); }
  @Input('advertise-id') set advertiseId(value: string) { this.loadAdvertise(value); }
  searchParams = new SearchParams();
  canonical: string;
  job: any;
  advertise: any;
  taggedJobTitles: string[];
  searchedTagKeywords: string[];
  title: string;
  schema: any;
  breadcrumbData: any;
  keywords: string[];
  jobClickId = Guid.newGuid();
  user: any;
  applyRequestId: any;
  apply: any;
  applyMode = 'none';
  // ampAction: string;
  seekerJob: any;

  constructor(private route: ActivatedRoute, private router: Router, private jobService: JobService,
    private collectService: CollectService, public sharedService: SharedService, private favoriteService: FavoriteService,
    private applyService: ApplyService, private spinner: SpinnerService, private toastr: ToastrService,
    private analytics: AnalyticsService, private karpashaService: KarpashaService) {

    sharedService.navigationEnd$.pipe(takeUntil(this.unsubscribe$)).subscribe(async () => await this.init());

    if (!sharedService.isBrowser) return;

    AuthState.user$.pipe(takeUntil(this.unsubscribe$)).subscribe(async user => this.user = Utility.clone(user));
  }

  private unsubscribe$ = new Subject<void>();
  ngOnDestroy(): void {
    this.resetSeo();

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
  async ngOnInit() {
    this.isMainComponent = this.route.component == <any>this.constructor;
  }

  async init() {
    const routeParams = { ...this.route.snapshot.params, ...this.route.snapshot.queryParams };
    try { await this.load(routeParams.id); }
    catch (e) { console.error(e); return; }
    this.applyRequestId = routeParams['apply-request'];
  }

  isMainComponent: boolean;
  openSourceUrlTitle: string;
  agentTitle: string;
  isActive: boolean;
  showSubscribe: boolean;
  showSource: boolean;
  karpashaAgreed: boolean;
  jobSource: string;


  searchQuery: string;

  async load(jobId: string) {
    if (!jobId || jobId == this.job?.Id) { return; }
    this.spinner.show('job-detail');

    this.job = await this.jobService.get(jobId);
    if (!this.job)
      return;

    if (!this.preview)
      this.maskContactInfo();
    this.spinner.hide('job-detail');

    this.setViewParams();
    this.setUserApplyData();

    if (this.isMainComponent && !this.isActive) {
      const searchRedirect = JobService.getExpiredRedirect(this.job);
      if (this.sharedService.isBrowser)
        this.router.navigateByUrl(searchRedirect);
      else
        this.sharedService.redirectPermanent(searchRedirect);
      return;
    }

    this.searchParams = <SearchParams>this.job.SearchParams || {
      q: JobService.getSearchQuery(this.job),
      city: this.job.City,
      state: this.job.State,
      l: Utility.locationSearch(this.job.State, this.job.City)
    };

    this.taggedJobTitles = JobService.getTaggedJobTitles(this.job);
    this.searchedTagKeywords = JobService.getSearchedTags(this.job).filter(_ => !this.taggedJobTitles.includes(_));
    if (this.searchQuery)
      this.searchedTagKeywords.push(this.searchQuery);

    if (!this.job.Title)
      this.job.Title = this.job.Brief.substring(0, 50) + '...';

    this.setSeo();
    this.setJobStructuredData();

    if (!this.preview && this.isActive) {

      await this.collectService.jobClick(this.job.Id, this.route.snapshot.queryParams, this.jobClickId);
      this.analytics.gtmEvent('job-view', 'seeker', this.job?.SourceName, true);
      if (this.user) {
        this.favorite = (await this.favoriteService.search({ ItemIds: [jobId] })).total > 0;
      }
    }

    this.setUserApplyData();
    this.loadAdvertise(this.route.snapshot.queryParams['ad']);
  }

  private setViewParams() {
    this.isActive = this.preview || this.job.IsKarpashaJob || (this.job?.Status == JobStatus.Started && !this.job?.IsExpired);

    if (this.job.AgentName) {
      const title = this.job.AgentName;
      this.agentTitle = title.includes('کاریابی') ? title : 'موسسه‌ی کاریابی ' + title;
    }

    this.showSubscribe = !this.preview && this.searchParams && !this.job.IsKarpashaJob;
    this.showSource = this.job.IsIndexed && !this.agentTitle && !this.job.IsKarpashaJob;
    this.searchQuery = JobService.getSearchQuery(this.job);
    this.openSourceUrlTitle = this.job.HasContactInfo ? 'مشاهده آگهی اصلی' : 'تماس با کارفرما';
    let channel = 'سایت';
    if (this.job.IsTelegram) { channel = 'کانال تلگرام'; }
    else if (this.job.IsInstagram) { channel = 'صفحه اینستاگرام'; }
    this.jobSource = `${channel} "${this.job.SourceName}"`;
    // this.needsPreWrap = this.job.IsTelegram || Utility.extractText(this.job.Description).split('\n').length >= 3;
  }

  private maskContactInfo() {

    this.job.Title = Utility.maskContactInfo(this.job.Title);
    this.job.Description = Utility.maskContactInfo(this.job.Description);
    this.job.Company = Utility.maskContactInfo(this.job.Company);
  }

  async setUserApplyData() {
    if (!this.sharedService.isBrowser || !this.isMainComponent) { return; }


    if (this.user && this.job.IsKarpashaJob) {
      this.seekerJob = (await this.karpashaService.getMySeekerJobs({ JobIds: [this.job.Id] }))?.list?.[0];
    }

    if (this.job.IsAppliable) {
      this.apply = {};
      this.applyMode = 'apply';
      if (this.applyRequestId) {
        this.apply = await this.applyService.getByRequestId(this.applyRequestId);
      } else if (this.user) {
        if (this.user.Seeker) {
          this.apply = await this.applyService.getByJobId(this.job.Id) || {};
        } else { this.user.Seeker = {}; }
      }

      if (this.apply?.Id) {
        this.applyMode = (this.apply.Type == 1 && (!this.user || this.user.Id == this.apply.SeekerId)) ? 'bought' : 'applied';
      }
    }
  }

  async rejectRequest() {
    this.spinner.show('request');
    this.apply = await this.applyService.rejectRequest(this.apply.Id);
    this.spinner.hide('request');
  }
  async acceptRequest() {
    this.spinner.show('request');
    this.apply = await this.applyService.acceptRequest(this.apply.Id);
    this.spinner.hide('request');
  }

  private setJobStructuredData() {
    if (!this.job) return;

    var locReq = { '@type': 'Country', name: 'ایران' };
    if (!this.job.IsRemote) {
      if (this.job.City) locReq = { '@type': 'City', name: this.job.City };
      else if (this.job.State) locReq = { '@type': 'State', name: this.job.State };
    }
    var rangeSalary = this.job.MinSalary < this.job.MaxSalary;
    var minWage = 8000000;
    var degrees = this.job.EducationDegrees?.filter(_ => _);

    const description = this.job.Description?.replace(/(&nbsp;)/g, ' ').trim() || this.title.trim();
    this.schema = {
      '@context': 'http://schema.org',
      '@type': 'JobPosting',
      "@id": `${environment.app.url}/job/${this.job.Id}`,
      datePosted: new Date(this.job.StartTime || Date.now()).toISOString().split('T')[0],
      description: description || 'آگهی استخدام',
      hiringOrganization: {
        '@type': 'Organization',
        name: this.job.Company || 'نامشخص'
      },
      title: this.job.Title || this.job.Brief,
      validThrough: new Date(this.job.ExpirationTime).toISOString().split('T')[0],
      applicantLocationRequirements: locReq,
      employmentType: this.job.JobTypes?.length ? this.job.JobTypes : 'OTHER',
      jobLocation: {
        '@type': 'Place',
        address: {
          '@type': 'PostalAddress',
          streetAddress: this.job.Address || this.job.District || 'نامشخص',
          addressLocality: this.job.City || 'کل استان',
          addressRegion: this.job.State || 'کل کشور',
          postalCode: '00000',
          addressCountry: this.job.Location?.Country || 'ایران'
        }
      },
      jobLocationType: this.job.IsRemote ? 'TELECOMMUTE' : null,
      baseSalary: {
        '@type': 'MonetaryAmount',
        'currency': "IRR",
        'value': {
          '@type': 'QuantitativeValue',
          'value': !rangeSalary ? (this.job.MinSalary || minWage) * 10 : undefined,
          'minValue': rangeSalary ? (this.job.MinSalary || minWage) * 10 : undefined,
          'maxValue': rangeSalary ? (this.job.MaxSalary || minWage) * 10 : undefined,
          'unitText': 'MONTH'
        }
      },
      experienceRequirements: !(this.job.MinExperienceYear > 0) ? null : {
        "@type": "OccupationalExperienceRequirements",
        monthsOfExperience: this.job.MinExperienceYear * 12
      },
      educationRequirements: !degrees?.length ? null : {
        "@type": "EducationalOccupationalCredential",
        credentialCategory: degrees[0]
      },
      directApply: true
    };
  }

  private setSeo() {
    this.title = JobService.getPageTitle(<SearchParams>{ q: this.job.Title, state: this.job.State, city: this.job.City, jt: this.job.JobTypes, remote: this.job.IsRemote }, false);
    this.canonical = `${environment.app.url}/job/${this.job.Id}`;
    if (this.preview) return;

    this.sharedService.setTitle(this.title);
    this.sharedService.removeLink('rel=\'canonical\'');
    this.sharedService.addLink({ rel: 'canonical', href: this.canonical });
    this.sharedService.updateMeta('image', `${environment.app.apiUrl}job/image/${this.job.Code}`, '', 'og', 'twitter');

    this.keywords = this.job.RequiredSkills || this.job.Title.split(' ');

    this.sharedService.setKeywordsMeta(...[<string>this.job.State, <string>this.job.City, ...this.keywords.slice(0, 10)]);
    this.sharedService.setDescriptionMeta((<string>this.job.Brief || ''));
    this.sharedService.addGeneralMeta({ type: 'http-equiv', name: 'last-modified', content: new Date(this.job.UpdateTime).toUTCString() });

    this.breadcrumbData = Utility.getBreadcrumb(this.job.State, this.job.City, this.taggedJobTitles[0], {
      title: this.job.Title,
      link: [`/job/${this.job.Id}`]
    });

    // this.sharedService.setNoIndex(true);
  }
  private resetSeo() {
    this.sharedService.setTitle();
    this.sharedService.removeLink('rel=\'canonical\'');
    this.sharedService.removeLink('rel=\'amphtml\'');
    this.sharedService.updateMeta('image', 'https://karpishe.com/assets/images/logo/KarpisheLogo.jpg', '', 'og', 'twitter');

    this.sharedService.removeGeneralMeta({ type: 'http-equiv', name: 'last-modified' });
  }

  share() {
    if ((<any>navigator).share) {
      (<any>navigator).share({
        title: this.title,
        // text: this.job.Description,
        url: this.canonical
      }// ).then(() => this.toastr.info('با تشکر از اشتراک گذاری')
      )?.catch(() => this.copy());
    } else {
      this.copy();
    }
  }
  copy() {
    if (Utility.setClipboard(this.title + '\r\n' + this.canonical)) {
      this.toastr.success('آدرس آگهی کپی شد.');
    }
  }
  // toggleShowAgent() {
  //   setTimeout$(() => $('#job-detail-agent').slideToggle());
  // }
  getFavIconSrc(size = 24, justDomain = false) {
    if (!this.job) return;
    if (!justDomain && this.agentTitle && this.job.Image)
      return this.job.Image?.startsWith('http') ? this.job.Image : this.imageUrl + this.job.Image;
    if (this.job.SourceDomain)
      return `https://www.google.com/s2/favicons?sz=${size}&domain=${this.job.SourceDomain}`;
    return '';
  }
  async toggleShowContact() {
    if (this.job._contactLoaded == undefined) {
      this.spinner.show('contact');
      const contact = await this.jobService.getContact(this.job.Id, this.route.snapshot.queryParams);
      this.job = Object.assign(this.job, contact, { _contactLoaded: true });
      this.spinner.hide('contact');
    } else {
      this.job._contactLoaded = !this.job._contactLoaded;
    }
    setTimeout$(() => {
      try { $('#job-detail-contact').slideToggle() }
      catch {
        let elem = document.getElementById('job-detail-contact');
        elem.style.display = elem.style.display != 'block' ? 'block' : 'none';
      }
    });
  }

  async setFavorite() {
    this.favorite = await this.favoriteService.toggle({
      ItemId: this.job.Id,
      Type: ModelType.Job,
      ExpirationTime: this.job.ExpirationTime,
      Item: {
        title: this.job.Title,
        company: this.job.Company,
        state: this.job.State,
        city: this.job.City
      }
    });
    this.favoriteChange.emit(this.favorite);
  }

  get autoRedirect() { return localStorage.getItem('job-detail-autoRedirect') == 'yes'; }
  set autoRedirect(set: boolean) { localStorage.setItem('job-detail-autoRedirect', set ? 'yes' : 'no'); }

  back: (back?: boolean) => void = () => {
    if (history.length > 1)
      history.back();
    else
      this.router.navigateByUrl(JobService.getExpiredRedirect(this.job, false));
  };
  async replaceState(url: string) {
    this.analytics.gtmEvent('job-click', 'seeker', this.job?.SourceName);
    if (this.job?.IsSponsored)
      await this.collectService.jobClickRedirected(null, this.job.Id, true);
    setTimeout$(() => {
      if (Utility.mobile)
        window.location.replace(url);
      else window.location.href = url;
    }, 200);
    return false;
  }
  onReport() {
    setTimeout$(() => location.reload(), 2000);
  }

  async loadAdvertise(advertiseId: string) {
    if (!advertiseId || this.advertise) return
    this.advertise = await this.jobService.getAdvertise(advertiseId);
  }

  async applyKarpasha() {
    this.spinner.show('apply-karpasha');
    const seekerJob = this.seekerJob ? Utility.clone(this.seekerJob) : { SeekerId: this.user.Id, JobId: this.job.Id };
    this.seekerJob = await this.karpashaService.saveMySeekerJob(seekerJob);

    if (this.advertise) {
      this.analytics.gtmEvent('advertise-apply', 'seeker', this.advertise?.Title || this.advertise?.Id);
    }

    //if recruitlead status is warm or hot, show success message, if not redirect to karpash status page
    if (this.user.Seeker?.RecruitLead?.Status == 3 || this.user.Seeker?.RecruitLead?.Status == 4) {
      this.spinner.hide('apply-karpasha');
      this.toastr.success('درخواست شما با موفقیت ثبت شد.');
    } else {
      this.router.navigate([`/karpasha`, `status`], { queryParams: { applyJobId: this.job.Id } });
    }


  }
}
